import { z } from "zod";

/**
 * Organization-level switches and preferences, stored in Organization.settings (jsonb).
 * Every key has a default, so adding a key never needs a migration and old rows
 * keep parsing. Always read through parseOrgSettings().
 */
export const orgSettingsSchema = z
  .object({
    features: z
      .object({
        metering: z.boolean().default(true),
        indexation: z.boolean().default(true),
        serviceChargeReconciliation: z.boolean().default(true),
        invoicing: z.boolean().default(true),
        stackingPlan: z.boolean().default(true),
        ownerPortal: z.boolean().default(false),
        tenantPortal: z.boolean().default(false),
        /** A bérlő a portálon jelezheti, hogy befizette a számlát. */
        tenantPaymentClaims: z.boolean().default(true),
        /** A bérlő a portálon óraállást diktálhat be (fotóval). */
        tenantSelfReading: z.boolean().default(true),
      })
      .default({}),
    locale: z.enum(["hu", "en"]).default("hu"),
    currency: z.string().length(3).default("HUF"),
    /** Lejárati figyelmeztetések: hány nappal előtte szóljunk. */
    alerts: z
      .object({
        expiryDays: z.number().int().default(180),
        noticeDays: z.number().int().default(60),
        optionDays: z.number().int().default(60),
        guaranteeDays: z.number().int().default(45),
        enabled: z.boolean().default(true),
      })
      .default({}),
    /**
     * Betöltési időszak: amíg be van kapcsolva, a tévesen rögzített adatok törölhetők
     * (szerződés, bérlő, óra, szállító, módosítás, indexálás). Élesben kapcsold ki.
     */
    onboarding: z.boolean().default(false),
    /** Számlázási gyakorlat (a kibocsátó eddigi számláihoz igazítható). */
    billing: z
      .object({
        /**
         * A teljesítés napja: DUE_DATE = a fizetési határidő (időszakos elszámolás, áfa tv. 58. §),
         * ISSUE_DATE = a számla kelte (ha a kibocsátó eddig így számlázott).
         */
        fulfilmentDate: z.enum(["DUE_DATE", "ISSUE_DATE"]).default("DUE_DATE"),
        /**
         * A számla megjegyzése. Helyettesítők: {év}, {hónap}, {Hónap}, {időszak}, {szerződés}, {ingatlan}.
         * Üresen: „Szerződés: {szerződés} · {ingatlan}”.
         */
        invoiceNote: z.string().max(500).nullable().default(null),
        /** Az időszak a tétel nevébe kerüljön („Bérleti díj – 2026. november”), vagy csak a megjegyzésbe. */
        periodInLineName: z.boolean().default(true),
      })
      .default({}),
    /** Friendly micro-copy (little jokes, emojis in empty states). */
    playfulCopy: z.boolean().default(true),
  })
  .passthrough();

export type OrgSettings = z.infer<typeof orgSettingsSchema>;
export type OrgFeature = keyof OrgSettings["features"];

export function parseOrgSettings(raw: unknown): OrgSettings {
  const parsed = orgSettingsSchema.safeParse(raw ?? {});
  return parsed.success ? parsed.data : orgSettingsSchema.parse({});
}

/** Deep-merges a partial patch into existing settings and re-validates. */
export function mergeOrgSettings(current: unknown, patch: unknown): OrgSettings {
  const base = parseOrgSettings(current) as Record<string, unknown>;
  const p = (patch ?? {}) as Record<string, unknown>;
  const merged: Record<string, unknown> = { ...base, ...p };
  for (const k of ["features", "alerts", "billing"]) {
    if (p[k] && typeof p[k] === "object") merged[k] = { ...(base[k] as object), ...(p[k] as object) };
  }
  return orgSettingsSchema.parse(merged);
}
